import {
  Panel,
  PanelSection,
  PanelHeader,
  PanelTitle,
  PanelDescription,
  Box,
  Flex,
  Text,
  Stack,
  TextLabel,
  VisuallyHidden,
} from '@customerio/pluma-components/react';
import { withForm, Slider } from '@/components/form';
import { formOpts } from '../helpers/form';
import { useId } from 'react';

interface ToneSliderProps {
  name: string;
  form: unknown;
  label: string;
  description: string;
  leftLabel: string;
  rightLabel: string;
  canEdit?: boolean;
}

function ToneSlider({ name, form, label, description, leftLabel, rightLabel, canEdit = true }: ToneSliderProps) {
  const id = useId();
  const min = 1;
  const max = 5;

  return (
    <Stack gap="050">
      <TextLabel id={`${id}-label`} htmlFor={`${id}-slider`}>
        {label}
      </TextLabel>
      <Text color="subtle" size="s" id={`${id}-desc`}>
        {description}
        <VisuallyHidden>.</VisuallyHidden>
      </Text>
      <Flex gap="100" alignItems="center">
        <VisuallyHidden id={`${id}-hidden-label`}>
          Scale is from {min}, {leftLabel} to {max}, {rightLabel}
        </VisuallyHidden>
        <Box flexShrink="0">
          <TextLabel as="span" variant="secondary" whiteSpace="nowrap">
            {leftLabel}
          </TextLabel>
        </Box>
        <Box flex={1}>
          <Slider
            ariaDescribedby={`${id}-desc ${id}-hidden-label`}
            ariaLabelledby={`${id}-label`}
            name={name}
            api={form}
            minValue={min}
            maxValue={max}
            step={1}
            id={`${id}-slider`}
            isDisabled={!canEdit}
          />
        </Box>
        <Box flexShrink="0">
          <TextLabel as="span" variant="secondary" whiteSpace="nowrap">
            {rightLabel}
          </TextLabel>
        </Box>
      </Flex>
    </Stack>
  );
}

export const ToneInfo = withForm({
  ...formOpts,
  props: {
    canEdit: true,
  },
  render: function ToneInfo({ form, canEdit }) {
    return (
      <Panel>
        <PanelHeader>
          <PanelTitle>Tone & Voice</PanelTitle>
          <PanelDescription>Adjust the sliders to define your brand's communication style</PanelDescription>
        </PanelHeader>
        <PanelSection>
          <Stack gap="300">
            <ToneSlider
              name="formalityScore"
              form={form}
              label="Formality"
              description="How formal or casual your communication style is"
              leftLabel="Casual"
              rightLabel="Formal"
              canEdit={canEdit}
            />
            <ToneSlider
              name="humorScore"
              form={form}
              label="Humor"
              description="How playful or witty the tone is"
              leftLabel="Serious"
              rightLabel="Humorous"
              canEdit={canEdit}
            />
            <ToneSlider
              name="respectScore"
              form={form}
              label="Respect"
              description="How cheeky or bold the brand voice is"
              leftLabel="Irreverent"
              rightLabel="Very Respectful"
              canEdit={canEdit}
            />
            <ToneSlider
              name="energyScore"
              form={form}
              label="Energy"
              description="Overall vibe and enthusiasm level"
              leftLabel="Calm & Direct"
              rightLabel="Enthusiastic"
              canEdit={canEdit}
            />
          </Stack>
        </PanelSection>
      </Panel>
    );
  },
});
